import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { showSystemToast } from "../utils/system_toast"

// The task list index (/tasks/lists, task_list_index/index). Every row is
// server-rendered, so search, the scope chips and the sort only show, hide and
// reorder rows. Writes (create, rename, pin, file, folders) go to the existing
// JSON endpoints and then re-render the page.
const COLLAPSED_KEY = "jane:task-list-index:collapsed"

export default class extends Controller {
    static targets = ["search", "chip", "sort", "group", "archivedGroup", "row", "empty", "newList", "newFolder", "folderName"]
    static values = { listsUrl: String, foldersUrl: String, pinsUrl: String }

    connect() {
        this.activeScope = "all"
        this._restoreCollapsed()
        this.apply()
    }

    // --- Search, scope and sort -------------------------------------------

    selectScope(event) {
        this.activeScope = event.currentTarget.dataset.scope
        this.chipTargets.forEach((chip) => {
            const on = chip === event.currentTarget
            chip.classList.toggle("is-active", on)
            chip.setAttribute("aria-pressed", on ? "true" : "false")
        })
        this.apply()
    }

    apply() {
        const query = this.searchTarget.value.trim().toLowerCase()
        const archived = this.activeScope === "archived"
        this._sortRows()
        let shown = 0
        this.groupTargets.forEach((group) => {
            const isArchived = group.classList.contains("tli-group--archived")
            const isFolder = group.classList.contains("tli-group--folder")
            let visible = 0
            group.querySelectorAll(".tli-list-row").forEach((row) => {
                const scopes = row.dataset.scopes.split(" ")
                const inScope = archived ? isArchived : !isArchived && (this.activeScope === "all" || scopes.includes(this.activeScope))
                const match = inScope && (!query || row.dataset.name.includes(query))
                row.hidden = !match
                if (match) visible += 1
            })
            // An empty folder still shows while nothing narrows the page, so it
            // can be seen (and filled) right after it is made.
            const keepEmpty = isFolder && !query && this.activeScope === "all"
            group.hidden = archived ? !isArchived || visible === 0 : isArchived || (visible === 0 && !keepEmpty)
            shown += visible
        })
        this.emptyTarget.hidden = shown > 0 || this.groupTargets.some((group) => !group.hidden)
        this.emptyTarget.textContent = query ? "No list by that name." : "Nothing here."
    }

    searchKeydown(event) {
        if (event.key === "Escape" && this.searchTarget.value) {
            event.preventDefault()
            this.searchTarget.value = ""
            this.apply()
        } else if (event.key === "Enter") {
            const first = this.rowTargets.find((row) => !row.hidden && !row.closest(".tli-group")?.hidden)
            const link = first?.querySelector(".tli-name-link")
            if (link) {
                event.preventDefault()
                window.location.assign(link.href)
            }
        }
    }

    _sortRows() {
        const key = this.sortTarget.value
        const compare = {
            name: (a, b) => a.dataset.name.localeCompare(b.dataset.name),
            open: (a, b) => Number(b.dataset.open) - Number(a.dataset.open),
            overdue: (a, b) => Number(b.dataset.overdue) - Number(a.dataset.overdue) || Number(b.dataset.open) - Number(a.dataset.open),
            activity: (a, b) => Number(b.dataset.activity) - Number(a.dataset.activity)
        }[key]
        this.element.querySelectorAll(".tli-group-body").forEach((body) => {
            const rows = [...body.querySelectorAll(":scope > .tli-list-row")]
            rows.sort(compare).forEach((row) => body.insertBefore(row, body.querySelector(":scope > .tli-folder-empty")))
        })
    }

    // --- Folders: collapse --------------------------------------------------

    toggleFolder(event) {
        const group = event.currentTarget.closest(".tli-group")
        const collapsed = !group.classList.contains("is-collapsed")
        this._setCollapsed(group, collapsed)
        const ids = new Set(this._collapsedIds())
        collapsed ? ids.add(group.dataset.folderId) : ids.delete(group.dataset.folderId)
        try {
            localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...ids]))
        } catch {
            // Private windows can refuse storage; the fold just won't persist.
        }
    }

    _setCollapsed(group, collapsed) {
        group.classList.toggle("is-collapsed", collapsed)
        group.querySelector(".tli-group-toggle")?.setAttribute("aria-expanded", collapsed ? "false" : "true")
    }

    _collapsedIds() {
        try {
            return JSON.parse(localStorage.getItem(COLLAPSED_KEY) || "[]")
        } catch {
            return []
        }
    }

    _restoreCollapsed() {
        const ids = this._collapsedIds()
        this.groupTargets.forEach((group) => {
            if (group.dataset.folderId && ids.includes(group.dataset.folderId)) this._setCollapsed(group, true)
        })
    }

    // --- Inline forms ---------------------------------------------------------

    startNewList() {
        this._openInline(this.newListTarget)
    }

    startNewFolder() {
        this.pendingListId = null
        this._openInline(this.newFolderTarget)
    }

    // "New folder…" from a list's menu: the folder is made and the list filed
    // in it in one request.
    fileInNewFolder(event) {
        this.pendingListId = event.currentTarget.closest(".tli-list-row").dataset.listId
        this._openInline(this.newFolderTarget)
    }

    startRenameFolder(event) {
        const head = event.currentTarget.closest(".tli-group-head")
        head.querySelector(".tli-group-toggle").hidden = true
        this._openInline(head.querySelector(".tli-folder-rename"))
    }

    startRenameList(event) {
        const row = event.currentTarget.closest(".tli-list-row")
        row.querySelector(".tli-name-text").hidden = true
        this._openInline(row.querySelector(".tli-rename"))
    }

    inlineKeydown(event) {
        if (event.key !== "Escape") return
        event.preventDefault()
        this._closeInline(event.currentTarget.closest("form"))
    }

    // Focused on the next frame: the menu that started this hands focus back
    // to its own button as it closes, which would blur the field at once.
    _openInline(form) {
        form.hidden = false
        const input = form.querySelector("input")
        requestAnimationFrame(() => {
            input.focus()
            input.select()
            input.addEventListener("blur", () => setTimeout(() => this._closeInline(form), 150), { once: true })
        })
    }

    _closeInline(form) {
        if (!form || form.dataset.saving) return
        form.hidden = true
        if (form.classList.contains("tli-new-row")) form.querySelector("input").value = ""
        form.closest(".tli-list-row")?.querySelector(".tli-name-text")?.removeAttribute("hidden")
        form.closest(".tli-group-head")?.querySelector(".tli-group-toggle")?.removeAttribute("hidden")
    }

    // --- Writes ---------------------------------------------------------------

    createList(event) {
        event.preventDefault()
        const name = this._value(event.currentTarget)
        if (!name) return
        this._send(event.currentTarget, "POST", this.listsUrlValue, { name }).then((data) => {
            if (data?.switch_url) window.location.assign(data.switch_url)
        })
    }

    createFolder(event) {
        event.preventDefault()
        const name = this._value(event.currentTarget)
        if (!name) return
        this._send(event.currentTarget, "POST", this.foldersUrlValue, { name, task_list_id: this.pendingListId }).then((data) => data && this._reload())
    }

    renameFolder(event) {
        event.preventDefault()
        const name = this._value(event.currentTarget)
        const id = event.currentTarget.closest(".tli-group").dataset.folderId
        if (!name) return
        this._send(event.currentTarget, "PATCH", `${this.foldersUrlValue}/${id}`, { name }).then((data) => data && this._reload())
    }

    deleteFolder(event) {
        const group = event.currentTarget.closest(".tli-group")
        const name = group.querySelector(".tli-group-name").textContent
        if (!window.confirm(`Delete the folder "${name}"? Its lists stay, just unfiled.`)) return
        this._send(null, "DELETE", `${this.foldersUrlValue}/${group.dataset.folderId}`).then((data) => data && this._reload())
    }

    pinFolder(event) {
        const id = event.currentTarget.closest(".tli-group").dataset.folderId
        const pinned = event.currentTarget.dataset.pinned === "true"
        this._send(null, "PATCH", `${this.foldersUrlValue}/${id}`, { pinned }).then((data) => data && this._reload())
    }

    renameList(event) {
        event.preventDefault()
        const name = this._value(event.currentTarget)
        const row = event.currentTarget.closest(".tli-list-row")
        if (!name) return
        this._send(event.currentTarget, "PATCH", row.dataset.renameUrl, { name }).then((data) => data && this._reload())
    }

    fileList(event) {
        const row = event.currentTarget.closest(".tli-list-row")
        const folderId = event.currentTarget.dataset.folderId
        this._send(null, "PATCH", `/task_lists/${row.dataset.listId}/folder`, { folder_id: folderId || null }).then((data) => data && this._reload())
    }

    pin(event) {
        const row = event.currentTarget.closest(".tli-list-row")
        this._send(null, "POST", this.pinsUrlValue, { pinable_type: "TaskList", pinable_id: row.dataset.listId }).then((data) => data && this._reload())
    }

    unpin(event) {
        this._send(null, "DELETE", `${this.pinsUrlValue}/${event.currentTarget.dataset.pinId}`).then((data) => data && this._reload())
    }

    _value(form) {
        return form.querySelector("input").value.trim()
    }

    _send(form, method, url, body) {
        if (form) form.dataset.saving = "1"
        return fetch(url, {
            method,
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: body ? JSON.stringify(body) : undefined
        })
            .then((response) => response.json().catch(() => ({})).then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || data.ok === false) throw new Error(data.error || "That didn't save")
                return data
            })
            .catch((error) => {
                showSystemToast(error.message, { level: "error" })
                return null
            })
            .finally(() => {
                if (form) delete form.dataset.saving
            })
    }

    _reload() {
        window.location.reload()
    }
}
